import {FC} from 'react';
import {useTranslation} from 'react-i18next';
import Field from 'components/Form/Field';
import FormHeader from 'components/Form/FormHeader';
import KeyValue from 'components/Layout/KeyValue';
import LinkButton from 'components/LinkButton';
import NavigationTabs from 'components/Navigation/NavigationTabs';
import Value from 'components/Value';
import {useBackend} from 'state/backend';
import {DocumentArchiveBackend} from './types';

const FieldsLeft: FC = () => {
    const {
        data: {document, partner},
    } = useBackend<DocumentArchiveBackend>();
    const {t} = useTranslation('History.DocumentArchive.Item');

    return (
        <>
            <Field label={t('label.document')} type="value">
                <span>
                    {document.displayName}
                    <LinkButton
                        className="ml-2"
                        href={`/api/partner/${partner.mid}/archive/${document.mid}/pdf/`}
                        kind="secondary"
                        size="xs"
                        target="_blank"
                    >
                        {t('viewPdf')}
                    </LinkButton>
                </span>
            </Field>
            <Field label={t('label.transactionDate')} type="value">
                <Value
                    value={{
                        format: 'date',
                        type: 'date',
                        value: new Date(document.dateIssued),
                    }}
                />
            </Field>
            <Field label={t('label.client')} type="value">
                <Value
                    value={{
                        type: 'string',
                        value: document.clientName,
                    }}
                />
            </Field>
            <Field label={t('label.total')} type="value">
                <Value
                    value={{
                        currency: 'JPY',
                        type: 'currency',
                        value: document.total,
                    }}
                />
            </Field>
            <Field label={t('label.documentExternalId')} type="value">
                <Value
                    value={{
                        type: 'string',
                        value: document.externalId || '—',
                    }}
                />
            </Field>
        </>
    );
};

const FieldsRight: FC = () => {
    const {
        data: {document},
    } = useBackend<DocumentArchiveBackend>();
    const {t} = useTranslation('History.DocumentArchive.Item');

    return (
        <>
            <Field label={t('label.archivedAt')} type="value">
                <Value
                    value={{
                        format: 'datetime',
                        type: 'date',
                        value: new Date(document.dateSent),
                    }}
                />
            </Field>
            <Field label={t('label.sender')} type="value">
                <Value
                    value={{
                        type: 'string',
                        value: document.employee,
                    }}
                />
            </Field>
            <Field label={t('label.deliveryMethods')} type="value">
                <Value
                    value={{
                        type: 'string',
                        value: document.deliveryMethods.join(', '),
                    }}
                />
            </Field>
            <Field label={t('label.legalDeliveryCategories')} type="value">
                <Value
                    value={{
                        type: 'string',
                        value: document.legalCategories.join(', '),
                    }}
                />
            </Field>
        </>
    );
};

const DocumentArchiveItemPage: FC = () => {
    const {appPermissions} = useBackend<DocumentArchiveBackend>();
    const {t} = useTranslation('History.DocumentArchive.Item');

    return (
        <div className="flex min-h-screen flex-col">
            <NavigationTabs.History
                permissions={appPermissions}
                selection="documentArchive"
            />
            <FormHeader title={t('title')} />
            <div className="flex mt-4 flex-row text-base">
                <div className="flex-col flex-1 min-w-0">
                    <KeyValue>
                        <FieldsLeft />
                    </KeyValue>
                </div>
                <div className="flex-col flex-1 min-w-0">
                    <KeyValue>
                        <FieldsRight />
                    </KeyValue>
                </div>
            </div>
        </div>
    );
};

export default DocumentArchiveItemPage;
